import { Box, Group, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";
import type { ExportDataset } from "../../lib/api";
import { DataTable, type Column } from "./DataTable";
import { ExportMenu } from "./ExportMenu";

/**
 * A list, the same way everywhere: a table on the desktop, cards on a phone.
 *
 * WHY THIS EXISTS
 *
 * The service queue got a table first and the others did not, so the app had
 * one screen that behaved like a spreadsheet and five that behaved like a
 * phone at 1440px. "Make it consistent" is the requirement, and the way to
 * make consistency hold is to stop each screen deciding: a screen supplies
 * its columns and its card, and gets the same arrangement, the same export
 * and the same keyboard behaviour as every other list.
 *
 * WHAT A SCREEN STILL OWNS
 *
 * Its columns, its card, its empty state, and where a row goes when opened.
 * Those are the parts that differ between an order and a delivery. Everything
 * around them - the breakpoint, the export button and its wording, the note
 * under the table - is not a per-screen decision and is not offered as one.
 *
 * THE EXPORT IS THE WHOLE LIST
 *
 * It used to say "Export 47 rows" and write a CSV of exactly those 47. That
 * was honest about what it did and useless: 47 was the capped page, not the
 * list. A screen now names its dataset and the server builds the file from
 * every row - see ExportMenu, and src/services/export.service.ts.
 */
export function ListView<T>({
  rows,
  columns,
  rowKey,
  onOpen,
  renderCard,
  exportDataset,
  caption,
  empty,
  emptyLabel = "Nothing matches.",
  activeKey,
  rowMuted,
  footnote,
  actions,
}: {
  rows: readonly T[];
  columns: readonly Column<T>[];
  rowKey: (row: T) => string;
  onOpen?: (row: T) => void;
  /** The phone's row. A table at 390px is a table nobody can read. */
  renderCard: (row: T) => ReactNode;
  /**
   * Which server-side export backs this list. The file's name, columns and
   * contents are all the dataset's; a screen chooses the list, not the file.
   */
  exportDataset: ExportDataset;
  caption: string;
  /** Shown instead of everything when there is nothing at all. */
  empty?: ReactNode;
  emptyLabel?: string;
  activeKey?: string;
  rowMuted?: (row: T) => boolean;
  /** A quiet line under the table. Reference, not instruction. */
  footnote?: ReactNode;
  /** Screen-specific controls, above the table on the desktop. */
  actions?: ReactNode;
}) {
  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <>
      <Box visibleFrom="md">
        <Stack gap={12}>
          <Group gap="sm" wrap="nowrap" align="flex-end">
            <Box style={{ flex: 1, minWidth: 0 }}>{actions}</Box>
            {/* NOT disabled on an empty screen. A filter that matches
                nothing says nothing about whether the list has rows, and
                this export ignores the filters - so the one moment somebody
                most wants the whole list is the moment the old button
                greyed itself out. */}
            <ExportMenu dataset={exportDataset} />
          </Group>

          <DataTable
            rows={rows}
            columns={columns}
            rowKey={rowKey}
            {...(onOpen ? { onOpen } : {})}
            {...(activeKey !== undefined ? { activeKey } : {})}
            {...(rowMuted ? { rowMuted } : {})}
            caption={`${caption}: ${rows.length}`}
            emptyLabel={emptyLabel}
          />

          <Text fz={12} c="gray.6">
            {onOpen ? "j / k or the arrow keys move, Enter opens. " : ""}
            {footnote}
          </Text>
        </Stack>
      </Box>

      <Box hiddenFrom="md">
        <Stack gap="sm">{rows.map((row) => renderCard(row))}</Stack>
      </Box>
    </>
  );
}
